import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    ArrowRight,
    Zap,
    Volume2,
    MessageCircle,
    Filter,
    CheckCircle2,
    Trophy,
    Sparkles,
    ClipboardList,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { isPaywallError } from "@/lib/access";
import { useQuotas } from "@/hooks/use-quotas";
import { ttsLanguageOf, voiceOf } from "@/lib/lang";
import { useDashboard } from "@/contexts/DashboardContext";

/* -------------------------- BLOCK VIEW ----------------------------------- */

function WordRow({ w, index, prefix }) {
    return (
        <li className={`px-3 py-2 rounded-xl text-sm ${w.priority ? "bg-emerald/8 border border-emerald/25" : "bg-paperAlt border border-lexiBorder"}`} data-testid={`intensive-${prefix}-word-${index}`}>
            <div className="flex items-start gap-2">
                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${w.priority ? "bg-emerald text-white" : "bg-white border border-lexiBorder text-inkSoft"}`}>
                    {w.priority ? "PRIO" : "RECO"}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-serif text-base">{w.word}</span>
                        <span className="text-inkSoft">— {w.translation_fr}</span>
                        {w.ipa && <span className="text-[11px] text-inkSoft">{w.ipa}</span>}
                    </div>
                    {w.natural_sentence && (
                        <p className="text-[11px] text-inkSoft italic mt-0.5">
                            « {w.natural_sentence} » {w.sentence_translation_fr ? `— ${w.sentence_translation_fr}` : ""}
                        </p>
                    )}
                </div>
            </div>
        </li>
    );
}

function BlockView({ session, block, blockIndex, onQuizStart, onNext, onAudioMode, onOpenConversation, onFilter, filterDifficult }) {
    const priority = block.words.filter((w) => w.priority);
    const recognition = block.words.filter((w) => !w.priority);
    const totalBlocks = session.plan.blocks;

    return (
        <div className="max-w-3xl mx-auto" data-testid={`intensive-block-${blockIndex}`}>
            <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">
                Bloc {blockIndex + 1} / {totalBlocks} · {block.words.length} mots · {priority.length} prioritaires
            </p>
            <h2 className="font-serif text-2xl md:text-3xl mt-1 leading-tight">
                {block.block_theme_fr}
            </h2>
            {block.mnemonic_scene && (
                <p className="mt-2 text-sm text-inkSoft italic" data-testid="intensive-mnemonic">
                    {block.mnemonic_scene}
                </p>
            )}

            {/* Toolbar */}
            <div className="mt-4 flex flex-wrap gap-2">
                <button
                    onClick={onAudioMode}
                    data-testid="intensive-audio-mode"
                    className="inline-flex items-center gap-1.5 text-xs bg-ink text-white hover:bg-emerald rounded-full px-3 py-1.5 transition-colors"
                >
                    <Volume2 className="w-3.5 h-3.5" /> Mode audio
                </button>
                <button
                    onClick={onOpenConversation}
                    data-testid="intensive-conversation-mode"
                    className="inline-flex items-center gap-1.5 text-xs bg-white border border-lexiBorder hover:bg-paperAlt rounded-full px-3 py-1.5"
                >
                    <MessageCircle className="w-3.5 h-3.5" /> Mode conversation
                </button>
                <button
                    onClick={onFilter}
                    data-testid="intensive-filter-difficult"
                    className={`inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border ${filterDifficult ? "bg-terracotta text-white border-terracotta" : "bg-white border-lexiBorder hover:bg-paperAlt"}`}
                >
                    <Filter className="w-3.5 h-3.5" />
                    {filterDifficult ? "Voir tout" : "Seulement les mots difficiles"}
                </button>
            </div>

            {/* Priority words */}
            <div className="mt-6">
                <p className="text-[10px] tracking-[0.22em] uppercase text-emerald font-semibold mb-2">
                    Mots prioritaires ({priority.length})
                </p>
                <ul className="space-y-2" data-testid="intensive-priority-list">
                    {(filterDifficult ? priority.filter((w) => block.difficult_words?.includes(w.word)) : priority).map((w, i) => (
                        <WordRow key={i} w={w} index={i} prefix="prio" />
                    ))}
                </ul>
            </div>

            {/* Recognition words */}
            {recognition.length > 0 && (
                <div className="mt-6">
                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mb-2">
                        Mots reconnaissance ({recognition.length})
                    </p>
                    <ul className="space-y-2" data-testid="intensive-recognition-list">
                        {(filterDifficult ? recognition.filter((w) => block.difficult_words?.includes(w.word)) : recognition).map((w, i) => (
                            <WordRow key={i} w={w} index={i} prefix="reco" />
                        ))}
                    </ul>
                </div>
            )}

            {/* CTA */}
            <div className="mt-8 flex flex-wrap justify-end gap-2">
                {!block.completed ? (
                    <button
                        onClick={onQuizStart}
                        data-testid="intensive-quiz-start"
                        className="inline-flex items-center gap-2 bg-emerald hover:bg-emeraldSoft text-white rounded-full px-5 py-2.5 text-sm transition-colors"
                    >
                        Quiz du bloc <ArrowRight className="w-4 h-4" />
                    </button>
                ) : blockIndex + 1 < totalBlocks ? (
                    <button
                        onClick={onNext}
                        data-testid="intensive-next-block"
                        className="inline-flex items-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-5 py-2.5 text-sm transition-colors"
                    >
                        Bloc suivant <ArrowRight className="w-4 h-4" />
                    </button>
                ) : (
                    <button
                        onClick={onNext}
                        data-testid="intensive-global-test-start"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm transition-colors"
                    >
                        Passer au test global <Trophy className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

/* -------------------------- QUIZ VIEW ------------------------------------ */

function QuizView({ questions, onSubmit, testIdPrefix, title_fr }) {
    const [answers, setAnswers] = useState(() => new Array(questions.length).fill(null));
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    async function submit() {
        if (answers.some((a) => a === null)) {
            toast.error("Répondez à toutes les questions.");
            return;
        }
        setBusy(true);
        try {
            const res = await onSubmit(answers.map((a) => Number(a)));
            setResult(res);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="max-w-2xl mx-auto" data-testid={testIdPrefix}>
            <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">Quiz</p>
            <h2 className="font-serif text-2xl mt-1">{title_fr}</h2>
            <ul className="mt-6 space-y-4">
                {questions.map((q, i) => (
                    <li key={i} className="bg-white border border-lexiBorder rounded-2xl p-4">
                        <p className="font-serif" data-testid={`${testIdPrefix}-q-${i}`}>
                            {i + 1}. {q.prompt_fr}
                            {q.priority && (
                                <span className="ml-2 text-[10px] tracking-[0.22em] uppercase text-emerald">
                                    prioritaire
                                </span>
                            )}
                        </p>
                        <div className="mt-2 space-y-1.5">
                            {(q.choices_target || []).map((c, j) => {
                                const isSelected = answers[i] === j;
                                const graded = result?.graded?.[i];
                                const isCorrect = graded && j === Number(graded.correct_index);
                                const isWrong = graded && isSelected && !graded.correct;
                                return (
                                    <button
                                        key={j}
                                        onClick={() => {
                                            if (result) return;
                                            const copy = [...answers];
                                            copy[i] = j;
                                            setAnswers(copy);
                                        }}
                                        disabled={!!result}
                                        data-testid={`${testIdPrefix}-q-${i}-choice-${j}`}
                                        className={`w-full text-left px-3 py-2 rounded-xl text-sm border transition-colors ${
                                            isCorrect
                                                ? "bg-emerald text-white border-emerald"
                                                : isWrong
                                                  ? "bg-terracotta/10 text-terracotta border-terracotta/30"
                                                  : isSelected
                                                    ? "bg-ink text-white border-ink"
                                                    : "bg-paperAlt border-lexiBorder hover:bg-paper"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                );
                            })}
                        </div>
                        {result?.graded?.[i]?.explanation_fr && (
                            <p className="mt-2 text-xs text-inkSoft italic">
                                {result.graded[i].explanation_fr}
                            </p>
                        )}
                    </li>
                ))}
            </ul>
            {!result ? (
                <button
                    onClick={submit}
                    disabled={busy}
                    data-testid={`${testIdPrefix}-submit`}
                    className="mt-6 inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-5 py-2.5 text-sm disabled:opacity-60 transition-colors"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Corriger
                </button>
            ) : (
                <p className="mt-6" data-testid={`${testIdPrefix}-score`}>
                    Score : <span className="font-serif text-3xl text-emerald">{result.score}</span> /100 ·{" "}
                    {result.correct_count} / {result.total} correctes.
                </p>
            )}
        </div>
    );
}

/* -------------------------- MAIN COMPONENT ------------------------------- */

export default function Intensive() {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [locked, setLocked] = useState(false);
    const [mode, setMode] = useState("block"); // block | quiz | global-test | done
    const [activeBlockIndex, setActiveBlockIndex] = useState(0);
    const [globalTestQuestions, setGlobalTestQuestions] = useState(null);
    const [globalTestResult, setGlobalTestResult] = useState(null);
    const [generatingBlock, setGeneratingBlock] = useState(false);
    const [filterDifficult, setFilterDifficult] = useState(false);
    const bootstrapped = useRef(false);
    const navigate = useNavigate();
    const { data: dash, refresh: refreshDashboard } = useDashboard();
    const voice = voiceOf(dash?.profile);
    const { data: quotas } = useQuotas();

    useEffect(() => {
        if (bootstrapped.current) return;
        bootstrapped.current = true;
        (async () => {
            try {
                const { data } = await api.post("/intensive/today/start");
                setSession(data.session);
                // Anchor on the highest-index block that ALREADY exists on the server.
                // The server can point current_block at a not-yet-generated slot after a
                // block quiz — the UI must not follow that until the block is generated.
                const blocksAvail = (data.session.blocks || []).length;
                const startIdx = Math.min(
                    Math.max(0, Number(data.session.current_block || 0)),
                    Math.max(0, blocksAvail - 1),
                );
                setActiveBlockIndex(startIdx);
                if (data.session.completed_at) {
                    setMode("done");
                    setGlobalTestResult(data.session.global_test?.result || null);
                }
            } catch (err) {
                if (isPaywallError(err)) {
                    setLocked(true);
                } else {
                    toast.error(formatApiError(err));
                    if (err?.response?.status === 400) navigate("/lesson");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [navigate]);

    const currentBlock = session?.blocks?.[activeBlockIndex] ?? null;

    async function refreshSession() {
        try {
            const { data } = await api.get(`/intensive/session/${session.session_id}`);
            setSession(data.session);
        } catch (err) {
            // Best-effort refresh — a transient network blip is fine. Log
            // 5xx so backend regressions show up during triage.
            if (err?.response?.status && err.response.status >= 500) {
                console.warn("[Intensive] refreshSession failed:", err);
            }
        }
    }

    async function startBlockQuiz() {
        try {
            const { data } = await api.post(
                `/intensive/session/${session.session_id}/block/${activeBlockIndex}/quiz/start`,
            );
            setSession((s) => {
                const next = { ...s };
                next.blocks = [...s.blocks];
                next.blocks[activeBlockIndex] = { ...next.blocks[activeBlockIndex], quiz: data.quiz };
                return next;
            });
            setMode("quiz");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function submitBlockQuiz(answers) {
        const { data } = await api.post(
            `/intensive/session/${session.session_id}/block/${activeBlockIndex}/quiz/submit`,
            { answers },
        );
        // Update block state locally — keep activeBlockIndex anchored on the SAME
        // block so the score renders and the user can click "Bloc suivant" to advance.
        // We deliberately do NOT follow data.current_block here: the server can point
        // it at a not-yet-generated block, which is what caused the blank page bug.
        setSession((s) => {
            const next = { ...s };
            next.blocks = [...s.blocks];
            next.blocks[activeBlockIndex] = {
                ...next.blocks[activeBlockIndex],
                quiz_result: data.result,
                completed: true,
            };
            return next;
        });
        return data.result;
    }

    async function advanceToNext() {
        const nextIdx = activeBlockIndex + 1;
        if (nextIdx < session.plan.blocks) {
            // Lazy-generate the next block if it's not already there
            const already = session.blocks[nextIdx];
            if (already) {
                setActiveBlockIndex(nextIdx);
                setMode("block");
                return;
            }
            setGeneratingBlock(true);
            try {
                const { data } = await api.post(
                    `/intensive/session/${session.session_id}/block/${nextIdx}/generate`,
                );
                setSession((s) => {
                    const next = { ...s };
                    const blocks = [...s.blocks];
                    blocks[nextIdx] = data.block;
                    next.blocks = blocks;
                    next.current_block = nextIdx;
                    return next;
                });
                setActiveBlockIndex(nextIdx);
                setMode("block");
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setGeneratingBlock(false);
            }
        } else {
            // Start global test
            try {
                const { data } = await api.post(
                    `/intensive/session/${session.session_id}/global-test/start`,
                );
                setGlobalTestQuestions(data.questions);
                setMode("global-test");
            } catch (err) {
                toast.error(formatApiError(err));
            }
        }
    }

    async function submitGlobalTest(answers) {
        const { data } = await api.post(
            `/intensive/session/${session.session_id}/global-test/submit`,
            { answers },
        );
        setGlobalTestResult(data.result);
        setSession(data.session);
        refreshDashboard?.();
        setMode("done");
        return data.result;
    }

    async function audioMode() {
        if (!currentBlock) return;
        const words = currentBlock.words.filter((w) => w.priority).slice(0, 10);
        toast.info(`Lecture audio de ${words.length} mots prioritaires…`);
        for (const w of words) {
            try {
                const { data } = await api.post("/poc/tts", {
                    text: w.word,
                    language: ttsLanguageOf(session.language_fr),
                    speed: "natural",
                    voice,
                });
                await new Promise((resolve) => {
                    const audio = new Audio(`data:${data.mime_type};base64,${data.audio_base64}`);
                    audio.addEventListener("ended", resolve);
                    audio.addEventListener("error", resolve);
                    audio.play();
                });
            } catch (err) {
                // One TTS word failing shouldn't break the sequence — skip
                // and continue. Log for visibility.
                console.warn("[Intensive] block TTS word playback failed:", err);
            }
        }
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="intensive-loading">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
                <p className="mt-3 text-sm text-inkSoft">Préparation du bloc…</p>
            </div>
        );
    }

    if (locked) {
        return (
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="intensive-locked">
                <button
                    onClick={() => navigate("/dashboard")}
                    className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                    data-testid="intensive-locked-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Tableau de bord
                </button>
                <div className="rounded-3xl border-2 border-ink bg-white p-8 md:p-10">
                    <div className="flex items-center gap-3 mb-4">
                        <Sparkles className="w-8 h-8 text-terracotta" strokeWidth={1.75} />
                        <p className="text-[11px] tracking-[0.28em] uppercase text-inkSoft font-semibold">
                            Mode intensif · réservé aux abonnés
                        </p>
                    </div>
                    <h1 className="font-serif text-3xl md:text-4xl leading-tight text-ink">
                        Passez la vitesse supérieure.
                    </h1>
                    <p className="mt-4 text-base text-inkSoft leading-relaxed">
                        Le mode intensif (50 à 100 mots par jour, blocs thématiques, quiz par bloc et test global) est
                        inclus dans l&apos;abonnement <span className="italic text-terracotta">Lexia School</span>.
                    </p>
                    <ul className="mt-4 space-y-1.5 text-sm text-ink">
                        <li>• 5 blocs quotidiens de 20 mots à haute intensité</li>
                        <li>• Quiz de bloc + test global chronométré</li>
                        <li>• Sélection prioritaire des mots à réviser</li>
                    </ul>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={() => navigate("/abonnement")}
                            data-testid="intensive-locked-upgrade"
                            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-3 text-sm font-medium transition-colors"
                        >
                            S&apos;abonner à Lexia School
                            <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                            onClick={() => navigate("/lesson")}
                            data-testid="intensive-locked-fallback-lesson"
                            className="inline-flex items-center gap-2 bg-white border border-lexiBorder hover:bg-paperAlt text-ink rounded-full px-5 py-3 text-sm font-medium transition-colors"
                        >
                            Ouvrir la leçon du jour à la place
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!session) return null;

    const isDone = mode === "done" || (session.completed_at && globalTestResult);
    const completedBlocks = session.blocks.filter((b) => b.completed).length;
    const totalBlocks = session.plan.blocks;

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10" data-testid="intensive-page">
            <header className="mb-6">
                <button
                    onClick={() => navigate("/dashboard")}
                    className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1"
                    data-testid="intensive-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Tableau de bord
                </button>
                <div className="mt-3">
                    <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">
                        <Zap className="w-3 h-3 inline mr-1" />
                        Mode intensif · {session.target_intensity} mots · Jour {session.program_day}
                    </p>
                    <h1 className="font-serif text-2xl md:text-3xl leading-tight" data-testid="intensive-title">
                        {session.theme_fr}
                    </h1>
                    <div className="mt-2 flex items-center gap-3 text-xs text-inkSoft">
                        <span>
                            Progression : {completedBlocks} / {totalBlocks} blocs
                        </span>
                        <div className="flex-1 max-w-xs h-1.5 bg-paperAlt rounded-full overflow-hidden" data-testid="intensive-progress">
                            <div
                                className="h-full bg-emerald"
                                style={{ width: `${Math.min(100, (completedBlocks / totalBlocks) * 100)}%` }}
                            />
                        </div>
                    </div>
                </div>
            </header>

            {generatingBlock && (
                <div className="mb-4 text-sm text-inkSoft flex items-center gap-2" data-testid="intensive-generating">
                    <Loader2 className="w-4 h-4 animate-spin text-terracotta" />
                    Génération du bloc suivant…
                </div>
            )}

            {mode === "block" && currentBlock && (
                <BlockView
                    session={session}
                    block={currentBlock}
                    blockIndex={activeBlockIndex}
                    onQuizStart={startBlockQuiz}
                    onNext={advanceToNext}
                    onAudioMode={audioMode}
                    onOpenConversation={() => navigate("/conversation")}
                    onFilter={() => setFilterDifficult((f) => !f)}
                    filterDifficult={filterDifficult}
                />
            )}

            {mode === "quiz" && currentBlock?.quiz?.questions && !currentBlock.completed && (
                <QuizView
                    questions={currentBlock.quiz.questions}
                    onSubmit={async (answers) => {
                        const result = await submitBlockQuiz(answers);
                        // Auto-return to the block view after 4s so the user sees the score
                        // then can click "Bloc suivant".
                        setTimeout(() => setMode("block"), 4000);
                        return result;
                    }}
                    testIdPrefix="intensive-block-quiz"
                    title_fr={`Quiz — ${currentBlock.block_theme_fr}`}
                />
            )}

            {mode === "quiz" && currentBlock?.completed && currentBlock?.quiz_result && (
                <div className="max-w-2xl mx-auto" data-testid="intensive-block-quiz-recap">
                    <p className="text-xs tracking-[0.22em] uppercase text-inkSoft">Résultat du quiz</p>
                    <h2 className="font-serif text-2xl mt-1">{currentBlock.block_theme_fr}</h2>
                    <p className="mt-4" data-testid="intensive-block-quiz-score">
                        Score : <span className="font-serif text-3xl text-emerald">{currentBlock.quiz_result.score}</span> /100 ·{" "}
                        {currentBlock.quiz_result.correct_count} / {currentBlock.quiz_result.total} correctes.
                    </p>
                    <button
                        onClick={() => setMode("block")}
                        data-testid="intensive-block-quiz-continue"
                        className="mt-4 inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-4 py-2 text-sm transition-colors"
                    >
                        Continuer <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {mode === "global-test" && globalTestQuestions && (
                <QuizView
                    questions={globalTestQuestions}
                    onSubmit={submitGlobalTest}
                    testIdPrefix="intensive-global-test"
                    title_fr="Test global de la journée"
                />
            )}

            {mode === "done" && globalTestResult && (
                <div className="max-w-2xl mx-auto" data-testid="intensive-done">
                    <div className="bg-emerald text-white rounded-2xl p-6 md:p-8">
                        <Trophy className="w-6 h-6 mb-3" strokeWidth={1.75} />
                        <p className="text-[11px] tracking-[0.28em] uppercase text-white/70">
                            Journée intensive terminée
                        </p>
                        <h2 className="font-serif text-3xl md:text-4xl mt-1">
                            {globalTestResult.score}/100 au test global
                        </h2>
                        <p className="mt-2 text-sm">
                            {globalTestResult.correct_count} / {globalTestResult.total} correctes · 
                            {" "}programme jour {session.program_day + 1} demain.
                        </p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            onClick={() => navigate("/dashboard")}
                            data-testid="intensive-done-dashboard"
                            className="inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-4 py-2 text-sm transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
                        </button>
                        <button
                            onClick={() => navigate("/reviews")}
                            data-testid="intensive-done-reviews"
                            className="inline-flex items-center gap-2 bg-white border border-lexiBorder hover:bg-paperAlt rounded-full px-4 py-2 text-sm"
                        >
                            <ClipboardList className="w-4 h-4" /> Voir mes révisions
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
